import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PersistentNotice } from '../components/ui/NoticeBar';

interface NoticeContextType {
  notices: PersistentNotice[];
  addNotice: (notice: Omit<PersistentNotice, 'id'>) => string;
  removeNotice: (id: string) => void;
  clearNotices: () => void;
  getNoticesForPage: (pageKey?: string) => PersistentNotice[];
}

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export const useNotice = () => {
  const context = useContext(NoticeContext);
  if (context === undefined) {
    throw new Error('useNotice must be used within a NoticeProvider');
  }
  return context;
};

interface NoticeProviderProps {
  children: React.ReactNode;
}

export const NoticeProvider: React.FC<NoticeProviderProps> = ({ children }) => {
  const [notices, setNotices] = useState<PersistentNotice[]>([]);

  // Load persistent notices from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('solofix_persistent_notices');
    if (stored) {
      try {
        const parsedNotices = JSON.parse(stored);
        // Filter out expired notices
        const now = Date.now();
        const validNotices = parsedNotices.filter((notice: PersistentNotice & { createdAt?: number }) => {
          if (!notice.autoExpire || !notice.createdAt) return true;
          return (now - notice.createdAt) < notice.autoExpire;
        });
        setNotices(validNotices);
      } catch (error) {
        console.error('Failed to parse stored notices:', error);
        localStorage.removeItem('solofix_persistent_notices');
      }
    }
  }, []);

  // Save notices to localStorage whenever notices change
  useEffect(() => {
    const persistentNotices = notices.filter(notice => notice.persistenceKey);
    if (persistentNotices.length > 0) {
      localStorage.setItem('solofix_persistent_notices', JSON.stringify(persistentNotices));
    } else {
      localStorage.removeItem('solofix_persistent_notices');
    }
  }, [notices]);

  const addNotice = useCallback((noticeData: Omit<PersistentNotice, 'id'>): string => {
    const id = `notice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const createdAt = noticeData.persistenceKey ? Date.now() : undefined;
    const notice: PersistentNotice & { createdAt?: number } = {
      ...noticeData,
      id,
      ...(createdAt !== undefined && { createdAt }),
    };

    setNotices(prev => {
      // If this is a persistent notice with the same key, replace it
      if (notice.persistenceKey) {
        const filtered = prev.filter(n => n.persistenceKey !== notice.persistenceKey);
        return [...filtered, notice];
      }
      return [...prev, notice];
    });

    // Auto-expire if specified
    if (notice.autoExpire) {
      setTimeout(() => {
        removeNotice(id);
      }, notice.autoExpire);
    }

    return id;
  }, []);

  const removeNotice = useCallback((id: string) => {
    setNotices(prev => prev.filter(notice => notice.id !== id));
  }, []);

  const clearNotices = useCallback(() => {
    setNotices([]);
    localStorage.removeItem('solofix_persistent_notices');
  }, []);

  const getNoticesForPage = useCallback((pageKey?: string) => {
    if (!pageKey) return notices.filter(notice => !notice.persistenceKey?.includes('page:'));
    return notices.filter(notice => 
      !notice.persistenceKey || 
      notice.persistenceKey === pageKey ||
      notice.persistenceKey.startsWith('global:')
    );
  }, [notices]);

  const value: NoticeContextType = {
    notices,
    addNotice,
    removeNotice,
    clearNotices,
    getNoticesForPage,
  };

  return (
    <NoticeContext.Provider value={value}>
      {children}
    </NoticeContext.Provider>
  );
};

// Convenience functions for common notice types
export const noticeHelpers = {
  groupApplicationSent: (groupName: string) => ({
    type: 'pending' as const,
    title: 'Groepsaanvraag verzonden',
    message: `Je bent nu aangemeld bij de groep "${groupName}". De groepseigenaar krijgt het verzoek, en kan dit accepteren of weigeren. Je ontvangt een mail wanneer er actie is ondernomen.`,
    persistenceKey: `page:group_application_${groupName}`,
    dismissible: true,
  }),

  groupRequestsWaiting: (count: number, groupName: string) => ({
    type: 'info' as const,
    title: 'Nieuwe lidmaatschapsverzoeken',
    message: `Je hebt ${count} nieuwe ${count === 1 ? 'verzoek' : 'verzoeken'} voor groep "${groupName}".`,
    persistenceKey: `page:group_manage_${groupName}`,
    dismissible: true,
  }),

  eventApproved: (eventTitle: string) => ({
    type: 'success' as const,
    title: 'Evenement gecontroleerd!',
    message: `Je evenement "${eventTitle}" is door het team gecontroleerd en blijft gewoon live staan.`,
    persistenceKey: 'global:event_approved',
    autoExpire: 7 * 24 * 60 * 60 * 1000, // 7 days
  }),

  eventRejected: (eventTitle: string, reason?: string) => ({
    type: 'warning' as const,
    title: 'Evenement offline gehaald',
    message: `Je evenement "${eventTitle}" is door het team offline gehaald.${reason ? ` Reden: ${reason}` : ''}`,
    persistenceKey: 'global:event_rejected',
    autoExpire: 7 * 24 * 60 * 60 * 1000, // 7 days
  }),

  groupApplicationAccepted: (groupName: string) => ({
    type: 'success' as const,
    title: 'Geaccepteerd in groep!',
    message: `Je bent geaccepteerd in groep "${groupName}". Je kunt nu deelnemen aan de groepschat.`,
    persistenceKey: 'global:group_accepted',
    autoExpire: 3 * 24 * 60 * 60 * 1000, // 3 days
  }),

  groupApplicationRejected: (groupName: string) => ({
    type: 'info' as const,
    title: 'Groepsaanvraag afgewezen',
    message: `Je aanvraag voor groep "${groupName}" is afgewezen door de groepseigenaar.`,
    persistenceKey: 'global:group_rejected',
    autoExpire: 3 * 24 * 60 * 60 * 1000, // 3 days
  }),

  groupRejectedCannotReapply: (groupName: string) => ({
    type: 'warning' as const,
    title: 'Geen nieuwe aanvraag mogelijk',
    message: `Je kunt niet opnieuw aanmelden voor groep "${groupName}" omdat je eerder bent afgewezen door de groepseigenaar.`,
    persistenceKey: `page:group_rejected_${groupName}`,
    dismissible: true,
  }),
};